import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { House, Sparkle, Camera, Microphone, QrCode, MagnifyingGlass, MapPin, Heart, ArrowRight, Check, Quotes, GooglePlayLogo } from "@phosphor-icons/react";
import MarketingLayout from "@/components/MarketingLayout";
import SEO from "@/components/SEO";

export default function Landing() {
  return (
    <MarketingLayout>
      <SEO
        title="A place for everything"
        description="Nestled is the cozy home-inventory app. Snap a photo, name the spot, and never lose a thing again. Free for 25 items, Pro for unlimited everything."
        path="/"
      />
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="landing-hero">
        <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-terracotta-50 blur-3xl opacity-70" />
        <div className="absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-sage-400/20 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-12 md:pt-20 pb-16">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <motion.span initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-terracotta-50 text-terracotta-600 text-xs font-bold uppercase tracking-[0.18em]">
                <Sparkle weight="fill" size={12} /> Home, organized
              </motion.span>
              <motion.h1 initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
                className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl text-stoneink-900 font-semibold tracking-tight leading-[1.05]">
                A place for<br/>every little thing.
              </motion.h1>
              <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
                className="mt-5 text-lg text-stoneink-700 leading-relaxed max-w-lg">
                Remember where you tucked away the holiday lights, the spare batteries, your grandmother&apos;s recipe box. Nestled is the cozy inventory app for the home you actually live in.
              </motion.p>

              {/* Download Now — front and center, right below the hero copy */}
              <motion.div
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}
                className="mt-8 flex flex-wrap items-center gap-4"
                data-testid="landing-download-now-row"
              >
                <a
                  href="https://play.google.com/store/apps/details?id=app.nestled6035650c&pcampaignid=web_share"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="landing-download-now-btn"
                  className="group inline-flex items-center gap-3 h-14 pl-4 pr-6 rounded-2xl bg-stoneink-900 hover:bg-stoneink-900/90 text-white shadow-hover hover:shadow-hover transition-all active:scale-[0.98]"
                >
                  <GooglePlayLogo weight="fill" size={30} className="text-white shrink-0" />
                  <span className="text-left leading-tight">
                    <span className="block text-[10px] uppercase tracking-[0.16em] opacity-80">Available on</span>
                    <span className="block text-lg font-semibold -mt-0.5">Download Now</span>
                  </span>
                  <ArrowRight weight="bold" size={16} className="ml-1 opacity-70 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                </a>
                <div className="flex items-center gap-2 text-sm text-stoneink-500">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-sage-500 opacity-70"></span>
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-sage-500"></span>
                  </span>
                  Now live on Android · iPhone <Link to="/beta" className="underline underline-offset-4 hover:text-terracotta-600">coming soon</Link>
                </div>
              </motion.div>

              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
                className="mt-6 flex flex-wrap gap-3">
                <Link to="/register" data-testid="hero-get-started"
                  className="h-12 px-6 rounded-xl bg-terracotta-500 hover:bg-terracotta-600 text-white font-medium flex items-center gap-2 active:scale-[0.98] shadow-ambient">
                  Or start free on the web <ArrowRight weight="bold" size={16} />
                </Link>
                <Link to="/features" data-testid="hero-how-it-works"
                  className="h-12 px-6 rounded-xl bg-white border border-sand-300 hover:border-ochre-500 text-stoneink-900 font-medium flex items-center gap-2">
                  How it works
                </Link>
              </motion.div>

              <div className="mt-6 flex items-center gap-2 text-sm text-stoneink-500">
                <Check weight="bold" className="text-sage-600" size={14} /> 25 items free · no card needed
              </div>
            </div>

            <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2 }}
              className="relative">
              <div className="aspect-[4/5] rounded-3xl overflow-hidden border border-sand-300 shadow-hover bg-sand-200">
                <img src="https://images.unsplash.com/photo-1631630259742-c0f0b17c6c10?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwzfHxjb3p5JTIwaG9tZSUyMGludGVyaW9yJTIwd2FybXxlbnwwfHx8fDE3ODE3MjQxNzB8MA&ixlib=rb-4.1.0&q=85"
                  alt="Cozy organized home" className="w-full h-full object-cover" />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white border border-sand-300 rounded-2xl shadow-ambient p-4 max-w-[220px] hidden sm:block">
                <div className="flex items-center gap-2"><MapPin weight="duotone" className="text-sage-600" size={18} /><span className="text-sm font-medium text-stoneink-900">Garage › Shelf B</span></div>
                <div className="text-xs text-stoneink-500 mt-1">Holiday lights · warm white</div>
              </div>
              <div className="absolute -top-4 -right-4 bg-white border border-sand-300 rounded-2xl shadow-ambient p-3 hidden sm:flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-terracotta-50 text-terracotta-600 grid place-items-center"><Microphone weight="duotone" size={16} /></div>
                <div className="text-sm font-medium text-stoneink-900">&ldquo;Where are my…?&rdquo;</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF / STATS */}
      <section className="border-y border-sand-300 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            ["10×", "faster to find what you stored"],
            ["7 min", "average setup for a whole room"],
            ["Unlimited", "items on Pro"],
            ["100%", "of users sleep better"],
          ].map(([h, s]) => (
            <div key={s} className="flex flex-col items-center">
              <div className="font-heading text-3xl sm:text-4xl font-semibold text-terracotta-600">{h}</div>
              <div className="text-xs sm:text-sm text-stoneink-700 mt-1 max-w-[160px]">{s}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20" data-testid="landing-features">
        <div className="max-w-2xl">
          <span className="text-xs font-bold uppercase tracking-[0.18em] text-terracotta-600">Built for real homes</span>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl text-stoneink-900 font-semibold tracking-tight">Small features. Big calm.</h2>
          <p className="mt-4 text-stoneink-700">Everything you need, nothing you don&apos;t. No clutter inside an app about reducing clutter.</p>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            [Camera, "Photos of every thing", "Snap, tag, and tuck. A picture is worth a thousand frantic searches."],
            [Microphone, "Voice quick-add", "Hands full? Just say it. Whisper turns your voice into searchable text."],
            [MapPin, "Rooms inside rooms", "Garage › Shelf B › Box 2. Build it as deep as your house actually is."],
            [QrCode, "Printable QR labels", "Stick a code on the box. Scan it from your phone to see what&apos;s inside."],
            [MagnifyingGlass, "Find anything fast", "Search names, notes, tags. Filter by room or category."],
            [Heart, "Share with family", "Family plan keeps the whole household on the same shelf."],
          ].map(([Icon, title, body], i) => (
            <motion.div key={title}
              initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.04 }}
              className="rounded-3xl border border-sand-300 bg-white p-6 shadow-ambient hover:shadow-hover transition-all">
              <div className="h-12 w-12 rounded-2xl bg-terracotta-50 text-terracotta-600 grid place-items-center">
                <Icon weight="duotone" size={24} />
              </div>
              <h3 className="mt-5 font-heading text-xl text-stoneink-900 font-medium">{title}</h3>
              <p className="mt-2 text-stoneink-700 text-sm leading-relaxed">{body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-stoneink-900 text-white" data-testid="landing-how">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20">
          <div className="max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-[0.18em] text-terracotta-400">Three small steps</span>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">How Nestled works.</h2>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              ["Name a room", "Pantry, garage, the cabinet under the stairs — start with one place you actually use.", "01"],
              ["Add what&rsquo;s inside", "Snap a photo or dictate it. Tag, quantify, done in 10 seconds.", "02"],
              ["Print a QR label", "Stick it on the box. Future-you scans it and remembers everything.", "03"],
            ].map(([title, body, n]) => (
              <div key={title} className="rounded-3xl bg-white/5 border border-white/10 p-6 backdrop-blur-sm">
                <div className="font-heading text-5xl text-terracotta-400 font-semibold">{n}</div>
                <h3 className="mt-4 font-heading text-xl font-medium" dangerouslySetInnerHTML={{ __html: title }} />
                <p className="mt-2 text-white/75 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: body }} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="max-w-4xl mx-auto px-6 md:px-10 py-20">
        <Quotes weight="fill" size={36} className="text-terracotta-500" />
        <p className="mt-6 font-heading text-2xl sm:text-3xl text-stoneink-900 leading-relaxed font-medium">
          &ldquo;I used to spend twenty minutes looking for the spare HDMI cable. Now I just open Nestled, type &lsquo;hdmi,&rsquo; and it tells me <em>third drawer of the office desk</em>. It&apos;s the small win that I didn&apos;t know would feel this good.&rdquo;
        </p>
        <div className="mt-6 flex items-center gap-3 text-stoneink-700">
          <div className="h-10 w-10 rounded-full bg-ochre-400/30 text-ochre-600 grid place-items-center font-heading font-semibold">M</div>
          <div>
            <div className="font-medium text-stoneink-900">Maya R.</div>
            <div className="text-xs text-stoneink-500">family of 4 · Vancouver</div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-5xl mx-auto px-6 md:px-10 py-16">
        <div className="rounded-3xl bg-terracotta-500 text-white p-10 md:p-14 text-center shadow-hover relative overflow-hidden">
          <div className="absolute -top-16 -right-16 h-64 w-64 rounded-full bg-white/10 blur-2xl" />
          <div className="relative">
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">Your calmer home is one shelf away.</h2>
            <p className="mt-4 text-white/85 max-w-xl mx-auto">Free for 25 items. Pro for unlimited everything. Family to share it all with up to five people.</p>
            <Link to="/register" data-testid="final-cta-btn"
              className="mt-8 inline-flex items-center gap-2 h-12 px-7 rounded-xl bg-white hover:bg-sand-100 text-terracotta-600 font-medium transition-colors">
              Get started — it&apos;s free <ArrowRight weight="bold" size={16} />
            </Link>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
